import { throwIfCutShort, useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { getAllInbounds } from './apis'
import { ALL_INBOUNDS_FETCH_SIZE } from './consts'
import { AllInboundItem, GetAllInboundsReq } from './types'

// 메뉴 코드(gr08)를 키에 포함한다 — 메뉴 탭을 닫을 때 `MenuOnSessionWrap.remove` 가
// 쿼리 키에 코드가 들어간 캐시를 함께 지운다(탭을 닫으면 조회 결과도 남지 않는다).
const PATH = ['inbound', 'allInboundStatus', 'gr08'] as const

/** 조회 조건 — BE 구간(page·size)은 공용 훅이 채운다. 키에 넣지 않아 페이지를 넘겨도 다시 받지 않는다. */
export type AllInboundsCondition = Omit<GetAllInboundsReq, 'page' | 'size'>

export const allInboundStatusQueryKeys = {
  all: PATH,
  list: (condition: AllInboundsCondition | null) => [...PATH, 'LIST', condition] as const,
  /** 화주 목록 — 화면 필터 옵션 및 세션 화주의 서비스유형 조회에 쓴다. */
  shippers: (scope: string) => [...PATH, 'SHIPPERS', scope] as const,
}

/** 구간 경계에서 겹친 행을 거르는 기본키(InboundReport PK) — 모듈 상수라 참조가 바뀌지 않는다. */
const rowIdOf = (row: AllInboundItem): number => row.id

/**
 * GR05 입고조회(전체) 목록 — 전량 조회(LOGISPRD-15268). BE 상한(1,000)씩 끊어 받고 화면은 10,000건씩 나눈다.
 *
 * `condition` 이 없으면 조회하지 않는다 — 진입 즉시 조회하지 않고 [조회]·복원으로 조건이 서야 받는다(ST02·ST11 과 같은 근거).
 * 실패 안내는 공용 훅의 전역 알럿(서버 meta.message) 하나다 — 화면이 따로 띄우지 않는다.
 */
export const useGetAllInbounds = (condition: AllInboundsCondition | null): UseFetchAllRowsResult<AllInboundItem> =>
  useFetchAllRows({
    queryKey: allInboundStatusQueryKeys.list(condition),
    fetchPage: async (page, size = ALL_INBOUNDS_FETCH_SIZE) => {
      // enabled 가 condition !== null 이라 null 로는 불리지 않는다(단언 대신 spread — null 이면 조건 없는 요청이 아니라 훅이 끈다)
      const res = await getAllInbounds({ ...condition, page, size })
      // pageInfo 가 빠진 응답을 0건으로 메우지 않는다 — 0 이면 첫 구간에서 조용히 멈춘다. 없으면 여기서 던져 실패 알럿으로 드러낸다(rgr13 선례).
      const { items, pageInfo } = res.data
      throwIfCutShort(page, size, items.length, pageInfo.totalCount)
      return { items, totalCount: pageInfo.totalCount }
    },
    chunkSize: ALL_INBOUNDS_FETCH_SIZE,
    getKey: rowIdOf,
    enabled: condition !== null,
  })
